/**
 * How a design's words are laid out — the one place that decides it.
 *
 * A designed post is drawn *twice* in the composer: once by the renderer (the card
 * the author sees) and once by the text input that sits invisibly on top of it
 * (`view/com/composer/stylizer/DesignCanvas`). Those two have to agree to the
 * pixel, or the caret drifts away from the words it is supposed to be inside — and
 * a caret a character off is worse than no design mode at all.
 *
 * So they are not two calculations kept in step by hand. They are one function:
 * both layers read `designTextMetrics` for the type and `designSlotPadding` for the
 * whitespace, and `__tests__/layout.test.ts` renders the editor and fails if the
 * input and the drawn text disagree about a single value.
 *
 * The sizing is deliberately responsive rather than fixed. A design is a poster at
 * any width — a phone, a tablet, a desktop browser — so the type is chosen for the
 * words that are actually there: a short line is set large, and a long one shrinks
 * to fit the composition instead of overflowing it.
 */
import {type TextStyle} from 'react-native'

import {THMANYAH_LINE_HEIGHT, thmanyahFontFamily} from '#/state/postStyle/fonts'
import {
  type StyledPost,
  STYLED_POST_DEFAULT_SIZE,
  STYLED_POST_MAX_SIZE,
  STYLED_POST_MIN_SIZE,
} from '#/state/postStyle/types'

/** The colour a design's words are drawn in when the author chose none. */
export const DESIGN_DEFAULT_INK = '#1A1A1A'

/**
 * The size the canvas sets a short line at.
 *
 * The schema's own default, carried under the canvas's name so that the control which
 * changes the size (`DesignToolbar`) and the layout which decides it start from one
 * number rather than two that happen to agree.
 */
export const DESIGN_CANVAS_DEFAULT_SIZE = STYLED_POST_DEFAULT_SIZE

/**
 * How many lines a design is allowed to grow to before its type shrinks.
 *
 * Five, because that is where a card stops being a poster and starts being a page:
 * past it the answer is smaller type, not a taller card.
 */
export const DESIGN_MAX_LINES = 5

/**
 * How wide a character is, as a fraction of the type size.
 *
 * An average rather than a measurement — Arabic serif text averages a little over
 * half an em per character with its joins and marks — used only to decide whether
 * type has to shrink. Being approximately right is enough: the layout is still
 * exact (this affects the *size*, never the position, and the size is one number
 * both layers are handed).
 */
export const GLYPH_RATIO = 0.55

/** The smallest the type may shrink to before the card scrolls instead. */
export const DESIGN_MIN_SIZE = STYLED_POST_MIN_SIZE

/** The largest the type may be set at — the ceiling the record itself accepts. */
export const DESIGN_MAX_SIZE = STYLED_POST_MAX_SIZE

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/**
 * The type size a design's words are set at, for the width and the words themselves.
 *
 * A size the author chose is honoured for as long as the words fit the composition
 * (they may not run past `DESIGN_MAX_LINES` lines). Past that the type is reduced until
 * they do, and never below `DESIGN_MIN_SIZE`: a design whose words cannot fit even then
 * is a long post, and a long post is legible at the schema's floor.
 *
 * The design's *height* is not a budget here. A composition is its own size — the
 * width decides how the words break, and the card grows downwards with them — and a
 * design that shrank its type to fit a viewport would be a different design on every
 * device that looked at it.
 */
export function designFontSize(
  style: StyledPost,
  {chars, width}: {chars: number; width: number},
): number {
  const chosen = clamp(
    style.size ?? DESIGN_CANVAS_DEFAULT_SIZE,
    DESIGN_MIN_SIZE,
    DESIGN_MAX_SIZE,
  )
  if (chars <= 0 || width <= 0) return chosen

  /*
   * The type is the largest that fits, found by walking down from what the author
   * asked for. A closed form would be shorter and slightly wrong: the number of lines
   * is a *whole* number and the last line is partly empty, so at small sizes the
   * rounding is the answer — and an author a point above the floor must not be handed
   * a size that does not actually fit. The walk is at most the span of the schema's
   * range, and costs a comparison a step.
   */
  for (let size = chosen; size > DESIGN_MIN_SIZE; size--) {
    if (designEstimatedLines(chars, size, width) <= DESIGN_MAX_LINES)
      return size
  }
  return DESIGN_MIN_SIZE
}

/**
 * How many lines the layout expects words of this length to take at a size.
 *
 * An estimate — {@link GLYPH_RATIO} is an average, not a measurement — and exported
 * as one, because it is the number the decision above is made on: a test can check
 * that a size fits (`<= DESIGN_MAX_LINES`) rather than asserting one arithmetic
 * answer, and a later change to the average cannot make a passing test wrong.
 */
export function designEstimatedLines(
  chars: number,
  size: number,
  width: number,
): number {
  if (chars <= 0 || width <= 0) return 0
  const perLine = Math.max(1, width / (size * GLYPH_RATIO))
  return Math.ceil(chars / perLine)
}

/** The leading a size is drawn with, from the face's own measured ink. */
export function designLineHeight(size: number): number {
  return Math.round(size * THMANYAH_LINE_HEIGHT)
}

/** Everything about a design's type, as one value both layers are handed. */
export type DesignTextMetrics = {
  fontFamily: string
  fontSize: number
  lineHeight: number
  textAlign: 'center' | 'left' | 'right'
  /** The ink, for the drawn text. The editing layer renders its own text invisible. */
  color: string
}

/**
 * The type a design's words are drawn in, and typed in.
 *
 * `textAlign` is resolved here rather than at the two call sites because the two
 * call sites must not be able to disagree: `'auto'` in a *style* is the platform's
 * own start — right for Arabic, left for Latin — and both layers have to ask for the
 * same thing, so it is resolved once against the text that will be drawn.
 *
 */
export function designTextMetrics(
  style: StyledPost,
  {chars, width, text = ''}: {chars: number; width: number; text?: string},
): DesignTextMetrics {
  const fontSize = designFontSize(style, {chars, width})
  return {
    fontFamily: thmanyahFontFamily(style.weight),
    fontSize,
    lineHeight: designLineHeight(fontSize),
    textAlign:
      style.align === 'center' ? 'center' : isArabic(text) ? 'right' : 'left',
    color: style.ink ?? DESIGN_DEFAULT_INK,
  }
}

/**
 * Whether a stretch of text is Arabic — which is what decides the side a design's
 * words hang from when they are not centred.
 *
 * A design that is not centred follows its *text*, not the app's locale: the same
 * phone writes Arabic and English posts, and an English design centred on the left
 * edge would be a bug in either language.
 */
export function isArabic(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(
    text,
  )
}

/** Where a template's words sit, as the whitespace around them. */
export type DesignSlotPadding = {
  paddingTop: number
  paddingBottom: number
  paddingHorizontal: number
}

/**
 * The whitespace a design's words are given.
 *
 * Proportional to the card, so the same design breathes the same way on a phone and
 * on a desktop browser, with a floor so that a narrow canvas does not press its type
 * against the edge.
 */
export function designSlotPadding(
  width: number,
  {padTop = 0, padBottom = 0}: {padTop?: number; padBottom?: number} = {},
): DesignSlotPadding {
  const horizontal = Math.max(18, Math.round(width * 0.07))
  const vertical = Math.max(24, Math.round(width * 0.09))
  return {
    paddingTop: vertical + padTop,
    paddingBottom: vertical + padBottom,
    paddingHorizontal: horizontal,
  }
}

/**
 * The leading of a design's input, as a *browser* spells it.
 *
 * A browser's editor is a plain DOM node rather than an RN view, and React writes a
 * numeric `lineHeight` as the unitless CSS property: 75 becomes seventy-five times the
 * type size, which is a line box 3300px tall. The paragraph is then the height of the
 * whole composition and everything measured against it follows: the caret came out as
 * a bar down the canvas and a selection as a column over words it was never aligned
 * with. So the unit is written here, once, beside the number it belongs to — a device
 * is handed the points themselves (`designInputStyle`), and reads no CSS.
 */
export function designInputLineHeight(metrics: DesignTextMetrics): string {
  return `${metrics.lineHeight}px`
}

/**
 * The editing layer's own style: the design's type, drawn invisibly.
 *
 * Invisible on purpose, and not merely faint: the words the author sees are the
 * *design's* words (`PostDesignText`), with highlights, Mursala and the template
 * behind them. The input's job is the caret, the selection and the keyboard — so it
 * draws nothing, and every value that decides position is the one the design uses.
 */
export function designInputStyle(metrics: DesignTextMetrics): TextStyle {
  return {
    fontFamily: metrics.fontFamily,
    fontSize: metrics.fontSize,
    lineHeight: metrics.lineHeight,
    textAlign: metrics.textAlign,
    color: 'transparent',
    padding: 0,
    margin: 0,
    includeFontPadding: false,
    textAlignVertical: 'top',
    backgroundColor: 'transparent',
  }
}
